import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { intersection } from "lodash";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { ClientConfig } from "../../../../commons/libs/externals/veloconnect-proxy";
import { getBrandLogoUrl } from "../../../../commons/libs/resource-paths";
import { ActiveFilters, ProductFilterValues, type FilterValue } from "../../../../commons/specs/filters";
import { ProductSpecKey, ProductType } from "../../../../commons/specs/product";
import { AvailabilityFilterLocation } from "../../../../commons/types/availability";
import { AvailabilityFilterVisibility } from "../../../../commons/types/settings";
import { IconSmallInputError } from "../../../../resources/icons";
import imageLocationDealer from "../../../../resources/images/availability/availability-location-dealer.svg";
import imageLocationEverywhere from "../../../../resources/images/availability/availability-location-everywhere.svg";
import imageLocationSupplier from "../../../../resources/images/availability/availability-location-supplier.svg";
import Badge from "../../components/Badge/Badge";
import CheckboxListItem from "../../components/CheckboxListItem/CheckboxListItem";
import FilterModalValues from "../../components/FilterModal/FilterModalValues";
import FilterModalBrandsGrid from "../../components/FilterModalBrandsGrid/FilterModalBrandsGrid";
import FlexFooterLayout from "../../components/FlexFooterLayout/FlexFooterLayout";
import FlexLayout from "../../components/FlexLayout/FlexLayout";
import Headline from "../../components/Headline/Headline";
import Icon from "../../components/Icon/Icon";
import Image from "../../components/Image/Image";
import Note from "../../components/Note/Note";
import Paragraph from "../../components/Paragraph/Paragraph";
import RadioFieldWithImage from "../../components/RadioFieldWithImage/RadioFieldWithImage";
import RadioGroup from "../../components/RadioGroup/RadioGroup";
import {
  addActiveFilterValue,
  getDefaultFilterValuesForCombinedAssortmentFilter,
  getGracefulAvailabilityVisibility,
  isActiveFilter,
  removeActiveFilterValue,
  setActiveFilters,
  shouldIncludeSupplierFilters
} from "../../libs/filters";
import useOnMount from "../../libs/hooks/use-on-mount";
import { useBrands } from "../../libs/queries/use-brands";
import { useSettings } from "../../libs/queries/use-settings";
import { useCommonAppDispatcher, useCommonAppSelector } from "../../store/hooks";
import { useTrackingContext } from "../utils/tracking-context";
import { useVeloconnectEndpointsContext } from "../utils/veloconnect-endpoints-context";

type AvailabilityLocationMap = Record<AvailabilityFilterLocation, { label: string; image: string }>;
type FilterRenderConfig = { wawi: boolean; supplier: boolean };

interface Props<Type extends ProductType> {
  activeFilters: ActiveFilters<Type>;
  filterValues: NonNullable<ProductFilterValues<Type>[ProductSpecKey.CombinedAssortment]>;
  onFilterChange: (activeFilters: ActiveFilters<Type>) => void;
  disableDispatchOnAvailabilityLocationChange?: boolean;
}

const AvailabilityFilterModalPartial = <Type extends ProductType>({
  activeFilters,
  onFilterChange,
  filterValues,
  disableDispatchOnAvailabilityLocationChange = false
}: Props<Type>) => {
  const { t } = useTranslation(["commons"]);
  const [dispatch, actions] = useCommonAppDispatcher();
  const session = useCommonAppSelector(state => state.session);
  const veloconnectEndpoints = useVeloconnectEndpointsContext();
  const { mixpanel } = useTrackingContext();

  const { brand: brandKey } = useParams<{ brand?: string }>();
  const areMultipleBrandsDisplayed = brandKey === undefined;

  const settings = useSettings();

  const automaticAssortmentFilter = settings.initialized?.assortment?.automatic?.filter;

  const availabilityFilterVisibility = settings.assortmentFilterSettings.availabilityFilterVisibility;
  const [localAvailabilityLocation, setLocalAvailabilityLocation] = useState<AvailabilityFilterLocation>(
    session.availabilityFilter.availabilityLocation
  );
  const availabilityLocation = disableDispatchOnAvailabilityLocationChange
    ? localAvailabilityLocation
    : session.availabilityFilter.availabilityLocation;

  const wawiFilterValues = useMemo(
    () => filterValues.filter(({ value }) => value.startsWith("wawi_")),
    [filterValues] // prettier-ignore
  );

  const veloconnectFilterValues = useMemo(
    () => filterValues.filter(({ value }) => value.startsWith("veloconnect_")),
    [filterValues]
  );

  const hasWawiData = wawiFilterValues.length > 0;
  const hasVeloconnectData = veloconnectFilterValues.length > 0;

  const effectiveVisibility = useMemo(
    () => getGracefulAvailabilityVisibility(availabilityFilterVisibility, { hasWawiData, hasVeloconnectData }),
    [availabilityFilterVisibility, hasWawiData, hasVeloconnectData]
  );

  // Track once when graceful fallback is applied (setting doesn't match available data)
  const hasTrackedFallback = useRef(false);
  useEffect(() => {
    if (effectiveVisibility !== availabilityFilterVisibility && !hasTrackedFallback.current) {
      mixpanel?.trackAvailabilityVisibilityFallback(
        availabilityFilterVisibility,
        effectiveVisibility,
        hasWawiData,
        hasVeloconnectData
      );
      hasTrackedFallback.current = true;
    }
  }, [effectiveVisibility, availabilityFilterVisibility, hasWawiData, hasVeloconnectData, mixpanel]);

  // Determine which filter sections to render based on effective visibility and current location
  const filterRenderConfig = getFilterRenderConfig(effectiveVisibility, availabilityLocation);
  const shouldRenderWawiFilters = hasWawiData && filterRenderConfig.wawi;
  const shouldRenderSupplierFilters = hasVeloconnectData && filterRenderConfig.supplier;

  const areCredentialsInvalid = useMemo(() => {
    if (veloconnectEndpoints) {
      if (brandKey) {
        const endpoint = veloconnectEndpoints.getEndpointByBrandKey(brandKey);
        return endpoint?.areCredentialsInvalid ?? false;
      } else {
        const { endpointsWithInvalidCredentials } = veloconnectEndpoints;
        return (endpointsWithInvalidCredentials ?? []).length > 0;
      }
    } else {
      return false;
    }
  }, [veloconnectEndpoints, brandKey]);

  const areSupplierAvailabilitiesConfigurableForBrand = useMemo(() => {
    if (brandKey && veloconnectEndpoints) {
      const endpoint = veloconnectEndpoints.getEndpointByBrandKey(brandKey);
      if (endpoint) {
        return !endpoint.isConfigured && endpoint.isConfigurable;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }, [brandKey, veloconnectEndpoints]);

  // Show RadioGroup only when both data sources are available AND setting allows both
  const shouldShowRadioGroup =
    hasWawiData &&
    hasVeloconnectData &&
    (effectiveVisibility === "both_default_dealer" || effectiveVisibility === "both_default_everywhere");

  // Show headers when:
  // - Single source mode: always show the header for context
  // - Both mode with "everywhere" selected: show both headers to distinguish sections
  const shouldShowDealerHeader = effectiveVisibility === "dealer_only" || availabilityLocation === "everywhere";
  const shouldShowSupplierHeader = effectiveVisibility === "supplier_only" || availabilityLocation === "everywhere";

  const locationMap: AvailabilityLocationMap = useMemo(
    () => ({
      dealer: {
        label: t("commons:filterModalPartialProductFinder.automaticAssortment.locationMap.dealer"),
        image: imageLocationDealer
      },
      supplier: {
        label: t("commons:filterModalPartialProductFinder.automaticAssortment.locationMap.supplier"),
        image: imageLocationSupplier
      },
      everywhere: {
        label: t("commons:filterModalPartialProductFinder.automaticAssortment.locationMap.everywhere"),
        image: imageLocationEverywhere
      }
    }),
    [t]
  );

  /**
   * Handles the availability location change and set default active filter values.
   * This is okay and does not overwrite the initial default active filters for the automatic assortment which are dependent on settings,
   * because `onChange` does not trigger on initial render.
   */
  const handleAvailabilityLocationChange = useCallback(
    (location: AvailabilityFilterLocation) => {
      if (disableDispatchOnAvailabilityLocationChange) {
        setLocalAvailabilityLocation(location);
      } else {
        dispatch(actions.session.setAvailabilityFilterLocation(location));
      }

      const defaultFilterValues = getDefaultFilterValuesForCombinedAssortmentFilter<Type>(
        automaticAssortmentFilter,
        { [ProductSpecKey.CombinedAssortment]: filterValues } as ProductFilterValues<Type>,
        shouldIncludeSupplierFilters(settings.assortmentFilterSettings.availabilityFilterVisibility)
      ) as FilterValue<Type, ProductSpecKey.CombinedAssortment>[];

      const defaultWawiFilterValues = defaultFilterValues.filter(
        value => typeof value === "string" && value.startsWith("wawi_")
      );
      const defaultVeloconnectFilterValues = defaultFilterValues.filter(
        value => typeof value === "string" && value.startsWith("veloconnect_")
      );

      switch (location) {
        case "dealer":
          onFilterChange(
            setActiveFilters(activeFilters, {
              [ProductSpecKey.CombinedAssortment]: defaultWawiFilterValues
            })
          );
          break;
        case "supplier":
          onFilterChange(
            setActiveFilters(activeFilters, {
              [ProductSpecKey.CombinedAssortment]: defaultVeloconnectFilterValues
            })
          );
          break;
        case "everywhere":
          onFilterChange(
            setActiveFilters(activeFilters, {
              [ProductSpecKey.CombinedAssortment]: defaultFilterValues
            })
          );
          break;
      }
    },
    [
      disableDispatchOnAvailabilityLocationChange,
      automaticAssortmentFilter,
      filterValues,
      settings.assortmentFilterSettings.availabilityFilterVisibility,
      dispatch,
      actions.session,
      onFilterChange,
      activeFilters
    ]
  );

  useOnMount(() => {
    prefetchImages(locationMap);
  });

  return (
    <FlexFooterLayout
      footer={
        <FlexLayout gap="l" direction="column">
          {shouldRenderSupplierFilters &&
            areMultipleBrandsDisplayed &&
            veloconnectEndpoints?.configuredEndpoints &&
            (veloconnectEndpoints.configuredEndpoints ?? []).length > 0 && (
              <ConfiguredBrands configuredVeloconnectEndpoints={veloconnectEndpoints.configuredEndpoints} />
            )}

          {areCredentialsInvalid && (
            <Note kind="negative" icon={<Icon source={IconSmallInputError} />}>
              <Trans
                t={t}
                i18nKey="commons:filterModalPartialProductFinder.automaticAssortment.invalidCredentialsNotification"
                components={{ linebreak: <br /> }}
              />
            </Note>
          )}

          {areSupplierAvailabilitiesConfigurableForBrand && (
            <Note>
              <FlexLayout direction="column" gap="s" alignItems="flex-start">
                <Badge size="s" label={t("defaults.new")} />
                <Paragraph classNames={["u-width-60ch"]}>
                  <Trans
                    t={t}
                    i18nKey="commons:filterModalPartialProductFinder.automaticAssortment.supplierFilterConfigurableNotification"
                    components={{ linebreak: <br /> }}
                  />
                </Paragraph>
              </FlexLayout>
            </Note>
          )}
        </FlexLayout>
      }
    >
      <FlexLayout direction="column" gap="s">
        {shouldShowRadioGroup && (
          <RadioGroup<AvailabilityFilterLocation>
            gridCols={3}
            align="center"
            value={availabilityLocation}
            onChange={handleAvailabilityLocationChange}
          >
            <RadioFieldWithImage
              inlineRadio
              kind="support"
              key="dealer"
              image={<Image originalSize src={locationMap.dealer.image} />}
              value="dealer"
              label={locationMap.dealer.label}
            />
            <RadioFieldWithImage
              inlineRadio
              kind="support"
              key="supplier"
              image={<Image originalSize src={locationMap.supplier.image} />}
              value="supplier"
              label={locationMap.supplier.label}
            />
            <RadioFieldWithImage
              inlineRadio
              kind="support"
              key="everywhere"
              image={<Image originalSize src={locationMap.everywhere.image} />}
              value="everywhere"
              label={locationMap.everywhere.label}
            />
          </RadioGroup>
        )}

        {shouldRenderWawiFilters && (
          <AssortmentFilters
            type="dealer"
            filterValues={wawiFilterValues}
            activeFilters={activeFilters}
            onFilterChange={onFilterChange}
            showHeader={shouldShowDealerHeader}
          />
        )}

        {shouldRenderSupplierFilters && (
          <AssortmentFilters
            type="supplier"
            filterValues={veloconnectFilterValues}
            activeFilters={activeFilters}
            onFilterChange={onFilterChange}
            showHeader={shouldShowSupplierHeader}
          />
        )}
      </FlexLayout>
    </FlexFooterLayout>
  );
};

function AssortmentFilters<Type extends ProductType>({
  filterValues,
  activeFilters,
  onFilterChange,
  showHeader,
  type
}: {
  filterValues: NonNullable<ProductFilterValues<Type>[ProductSpecKey.CombinedAssortment]>;
  activeFilters: ActiveFilters<Type>;
  onFilterChange: (activeFilters: ActiveFilters<Type>) => void;
  showHeader: boolean;
  type: "supplier" | "dealer";
}) {
  const { t } = useTranslation(["commons"]);

  return (
    <FlexLayout direction="column" gap="s">
      {showHeader && (
        <Headline kind="minor">
          {t(`commons:filterModalPartialProductFinder.automaticAssortment.${type}FilterHeadline`)}
        </Headline>
      )}
      <FilterModalValues justifyContent="start" layout={{ type: "fixed", columnCount: 2 }}>
        {filterValues.map(filterValue => {
          const filterKey = ProductSpecKey.CombinedAssortment;
          const isActive = isActiveFilter(activeFilters, filterKey, filterValue.value);

          const handleClick = () =>
            onFilterChange(
              isActive
                ? removeActiveFilterValue(activeFilters, filterKey, filterValue.value)
                : addActiveFilterValue(activeFilters, filterKey, filterValue.value)
            );

          return (
            <CheckboxListItem
              key={`${filterKey}-${filterValue.value}`}
              outline
              label={filterValue.label}
              active={isActive}
              onClick={handleClick}
            />
          );
        })}
      </FilterModalValues>
    </FlexLayout>
  );
}

function ConfiguredBrands({ configuredVeloconnectEndpoints }: { configuredVeloconnectEndpoints: ClientConfig[] }) {
  const { t } = useTranslation(["commons"]);
  const brands = useBrands();
  const configuredBrandKeys = configuredVeloconnectEndpoints.flatMap(endpoint => endpoint.brandKeys);
  const configuredAndActiveBrandKeys = intersection(configuredBrandKeys, brands.activeKeys);

  return (
    <FilterModalBrandsGrid
      description={t("commons:availabilityFilterModalPartialProductFinder.brandsWithSupplierAvailabilities")}
      brands={configuredAndActiveBrandKeys.map(brandKey => ({
        key: brandKey,
        logoSrc: getBrandLogoUrl(brandKey)
      }))}
    />
  );
}

// Prefetch images in cache to avoid blocking the UI while the images are loading
async function prefetchImages(locationMap: AvailabilityLocationMap): Promise<void> {
  const promises = await Promise.all(
    Object.values(locationMap).map(location => {
      return new Promise(resolve => {
        const image = new window.Image();
        image.src = location.image;
        image.onload = resolve;
        image.onerror = resolve;
      });
    })
  );

  await Promise.all(promises);
}

/**
 * For "only" modes, visibility directly determines what to render.
 * For "both" modes, we defer to the location-based config.
 */
const visibilityRenderConfig: Record<AvailabilityFilterVisibility, FilterRenderConfig | "use-location"> = {
  dealer_only: { wawi: true, supplier: false },
  supplier_only: { wawi: false, supplier: true },
  both_default_dealer: "use-location",
  both_default_everywhere: "use-location"
};

/**
 * When in "both" mode, the selected location determines which filters to render.
 */
const locationRenderConfig: Record<AvailabilityFilterLocation, FilterRenderConfig> = {
  dealer: { wawi: true, supplier: false },
  supplier: { wawi: false, supplier: true },
  everywhere: { wawi: true, supplier: true }
};

function getFilterRenderConfig(
  visibility: AvailabilityFilterVisibility,
  location: AvailabilityFilterLocation
): FilterRenderConfig {
  const config = visibilityRenderConfig[visibility];
  return config === "use-location" ? locationRenderConfig[location] : config;
}

export default AvailabilityFilterModalPartial;
